import {
	Badge,
	Button,
	Card,
	Flex,
	SegmentedControl,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { ApexOptions } from 'apexcharts'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { getVersionStats } from 'src/apis/chartAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { color, size } from 'src/variable/constant'

interface VersionStat {
	version: string
	amount: number
}

interface PlatformStat {
	platform: string
	amount: number
}

function VersionStatsPage() {
	const { openError } = useModal()
	const [loading, setLoading] = useState(false)
	const [search, setSearch] = useState('')
	const [platform, setPlatform] = useState('ALL')
	const [sortBy, setSortBy] = useState('amount')
	const [stats, setStats] = useState<VersionStat[]>([])
	const [platforms, setPlatforms] = useState<PlatformStat[]>([])
	const [summary, setSummary] = useState({
		total: 0,
		unknown_amount: 0,
	})
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const handleGetStats = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getVersionStats({
				platform,
				search: search.trim(),
				sort_by: sortBy,
			})
			const object = res?.data?.results?.object || {}
			const data = Array.isArray(object.data) ? object.data : []
			const platformData = Array.isArray(object.platforms)
				? object.platforms
				: []

			setStats(
				data.map((item) => ({
					version: item.version || 'Unknown',
					amount: Number(item.amount) || 0,
				})),
			)
			setPlatforms(
				platformData.map((item) => ({
					platform: item.platform || 'Unknown',
					amount: Number(item.amount) || 0,
				})),
			)
			setSummary({
				total: Number(object.total) || 0,
				unknown_amount: Number(object.unknown_amount) || 0,
			})
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [openError, platform, search, sortBy])

	const platformOptions = useMemo(() => {
		const platformMap = new Map(
			platforms.map((item) => [item.platform, item.amount]),
		)

		return ['ALL', 'WEB', 'APP'].map((item) => ({
			value: item,
			label: `${item} (${Number(platformMap.get(item) || 0).toLocaleString(
				'en-US',
			)})`,
		}))
	}, [platforms])

	const platformLabel = platform === 'ALL' ? 'all platforms' : platform

	const tableData = useMemo(
		() =>
			[...stats]
				.map((item, index) => ({
					...item,
					no: index + 1,
					percent:
						summary.total > 0
							? `${((item.amount / summary.total) * 100).toFixed(2)}%`
							: '0.00%',
				})),
		[stats, summary.total],
	)

	const chartStats = useMemo(() => stats.slice(0, 10), [stats])

	const pageData = useMemo(() => {
		const start = (pagination.pageIndex - 1) * pagination.pageSize
		return tableData.slice(start, start + pagination.pageSize)
	}, [pagination, tableData])

	const chartOptions = useMemo<ApexOptions>(
		() => ({
			chart: {
				type: 'bar',
				height: 420,
				toolbar: {
					show: true,
					tools: {
						download: false,
						selection: false,
						zoom: false,
						zoomin: true,
						zoomout: true,
						pan: false,
					},
				},
			},
			plotOptions: {
				bar: {
					borderRadius: 4,
					columnWidth: '52%',
				},
			},
			dataLabels: {
				enabled: true,
			},
			xaxis: {
				categories: chartStats.map((item) => item.version),
				labels: {
					rotate: -35,
					trim: true,
				},
			},
			yaxis: {
				min: 0,
				labels: {
					formatter: (value) => Number(value).toLocaleString('en-US'),
				},
			},
			colors: [color.CmsPrimary],
			title: {
				text: `Users by Version - Top 10 (${platformLabel})`,
				align: 'left',
				style: {
					fontSize: '16px',
					color: '#666',
				},
			},
			tooltip: {
				y: {
					formatter: (value) => Number(value).toLocaleString('en-US'),
					title: {
						formatter: () => 'Users',
					},
				},
			},
		}),
		[chartStats, platformLabel],
	)

	const chartSeries = useMemo(
		() => [
			{
				name: 'Users',
				data: chartStats.map((item) => item.amount),
			},
		],
		[chartStats],
	)

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'version',
				header: 'Version',
				size: size.text,
				Cell: ({ cell }: any) => (
					<Badge
						color={cell.getValue() === 'Unknown' ? 'gray' : 'green'}
						variant="light"
					>
						{cell.getValue()}
					</Badge>
				),
			},
			{
				accessorKey: 'amount',
				header: 'Amount',
				size: size.text,
				Cell: ({ cell }: any) =>
					Number(cell.getValue() || 0).toLocaleString('en-US'),
			},
			{
				accessorKey: 'percent',
				header: '% of total',
				size: size.text,
			},
		],
		[],
	)

	const handleChangePage = useCallback((value: number) => {
		setPagination((prev) => ({
			...prev,
			pageIndex: value,
		}))
	}, [])

	useEffect(() => {
		const timer = setTimeout(() => {
			handleGetStats()
		}, 400)

		return () => clearTimeout(timer)
	}, [handleGetStats])

	useEffect(() => {
		setPagination((prev) => ({
			...prev,
			pageIndex: 1,
		}))
	}, [search, platform, sortBy])

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Flex justify="space-between" align="flex-end" gap={16} wrap="wrap">
				<Stack spacing={4}>
					<Text fw={700} size="lg">
						Version Stats
					</Text>
					<Text color="dimmed" size="sm">
						User count grouped by version and platform
					</Text>
				</Stack>
				<Flex gap={12} align="flex-end" wrap="wrap">
					<CustomClearInput
						value={search}
						onChange={setSearch}
						placeholder="Search version prefix"
					/>
					<CustomSelect
						value={sortBy}
						onChange={(value) => {
							setSortBy(value || 'amount')
						}}
						data={[
							{ value: 'amount', label: 'Amount' },
							{ value: 'version', label: 'Version' },
						]}
						clearable={false}
						sx={{ minWidth: 140 }}
					/>
					<Button
						variant="outline"
						leftIcon={<IconRefresh size={16} />}
						onClick={handleGetStats}
						loading={loading}
					>
						Refresh
					</Button>
				</Flex>
			</Flex>

			<SegmentedControl
				value={platform}
				onChange={setPlatform}
				data={platformOptions}
				fullWidth
				color="teal"
			/>

			<SimpleGrid
				cols={3}
				spacing="md"
				breakpoints={[
					{ maxWidth: 768, cols: 2 },
					{ maxWidth: 520, cols: 1 },
				]}
			>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Total users ({platformLabel})
						</Text>
						<Title order={3}>{summary.total.toLocaleString('en-US')}</Title>
					</Stack>
				</Card>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Total versions ({platformLabel})
						</Text>
						<Title order={3}>{stats.length.toLocaleString('en-US')}</Title>
					</Stack>
				</Card>
				<Card withBorder radius="md" p="md">
					<Stack spacing={4}>
						<Text size="sm" color="dimmed">
							Unknown users ({platformLabel})
						</Text>
						<Title order={3}>
							{summary.unknown_amount.toLocaleString('en-US')}
						</Title>
					</Stack>
				</Card>
			</SimpleGrid>

			<Card withBorder radius="md" p="md">
				<ReactApexChart
					options={chartOptions}
					series={chartSeries}
					type="bar"
					height={420}
				/>
			</Card>

			<TableComponent
				columns={columns}
				total={tableData.length}
				data={pageData}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				enableRowActions={false}
			/>
		</Stack>
	)
}

export default React.memo(VersionStatsPage)
